'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface TeamMember {
  id: number;
  name: string;
  role: string;
  specialization: string;
  image: string;
  alt: string;
  bio: string;
  countries: string[];
}

interface TeamCardProps {
  member: TeamMember;
  onSelect: (member: TeamMember) => void;
}

const TeamCard = ({ member, onSelect }: TeamCardProps) => {
  return (
    <div
      className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-hover transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
      onClick={() => onSelect(member)}>

      <div className="relative h-64 overflow-hidden">
        <AppImage
          src={member.image}
          alt={member.alt}
          className="w-full h-full object-cover" />

        <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 to-transparent"></div>
        <div className="absolute bottom-4 left-4 right-4 text-white">
          <h3 className="text-xl font-bold mb-1">{member.name}</h3>
          <p className="text-sm opacity-90">{member.role}</p>
        </div>
      </div>
      <div className="p-6">
        <div className="flex items-center gap-2 text-primary mb-3">
          <Icon name="StarIcon" size={16} variant="solid" />
          <span className="text-sm font-semibold">{member.specialization}</span>
        </div>
        <p className="text-muted-foreground text-sm line-clamp-2 mb-4">{member.bio}</p>
        <div className="flex flex-wrap gap-2">
          {member.countries.slice(0, 3).map((country, idx) =>
          <span key={idx} className="px-3 py-1 bg-muted text-foreground text-xs rounded-full">
              {country}
            </span>
          )}
          {member.countries.length > 3 &&
          <span className="px-3 py-1 bg-muted text-foreground text-xs rounded-full">
              +{member.countries.length - 3}
            </span>
          }
        </div>
      </div>
    </div>);

};

interface TeamSectionProps {
  className?: string;
}

const TeamSection = ({ className = '' }: TeamSectionProps) => {
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);

  const teamMembers: TeamMember[] = [
  {
    id: 1,
    name: 'Dr. Sarah Chen',
    role: 'Founder & CEO',
    specialization: 'Asian Education Systems',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_10a19f109-1763296358145.png",
    alt: 'Professional Asian woman with glasses in navy blazer smiling confidently in modern office setting',
    bio: 'PhD in International Education with 15 years of experience helping students navigate Asian university systems. Specialized in China, Japan, and Singapore pathways.',
    countries: ['China', 'Japan', 'Singapore', 'Vietnam']
  },
  {
    id: 2,
    name: 'Michael Rodriguez',
    role: 'Director of European Programs',
    specialization: 'European Universities',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1602376d6-1763294838474.png",
    alt: 'Hispanic man in charcoal suit with warm smile in contemporary office with natural lighting',
    bio: 'Former university admissions officer with deep connections across European institutions. Expert in German and French education systems and scholarship opportunities.',
    countries: ['Germany', 'France', 'Russia']
  },
  {
    id: 3,
    name: 'Priya Sharma',
    role: 'Senior Counselor - Southeast Asia',
    specialization: 'ASEAN Region Expert',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14b3a32aa-1772054739413.png",
    alt: 'Indian woman in burgundy blazer with professional demeanor in bright modern workspace',
    bio: 'Specialized in Southeast Asian education pathways with personal experience studying in Singapore and Philippines. Passionate about making quality education accessible.',
    countries: ['Singapore', 'Philippines', 'Vietnam']
  },
  {
    id: 4,
    name: 'Ahmed Al-Mansouri',
    role: 'Middle East Education Specialist',
    specialization: 'UAE & Gulf Region',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14e5fac11-1763301397423.png",
    alt: 'Middle Eastern man in white traditional dress with modern suit jacket in luxurious office environment',
    bio: 'Expert in UAE education system and Gulf region opportunities. Helps students understand the unique advantages of studying in the Middle East\'s education hubs.',
    countries: ['UAE']
  },
  {
    id: 5,
    name: 'Emma Thompson',
    role: 'Student Success Manager',
    specialization: 'Pre-Departure & Cultural Prep',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f8f896f2-1763299689286.png",
    alt: 'Caucasian woman with blonde hair in teal blouse smiling warmly in collaborative office space',
    bio: 'Focuses on preparing students for cultural transitions and ensuring smooth adaptation to international environments. Former international student herself.',
    countries: ['All Destinations']
  },
  {
    id: 6,
    name: 'Kenji Tanaka',
    role: 'Language & Cultural Advisor',
    specialization: 'East Asian Culture',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11398f23b-1763295770186.png",
    alt: 'Japanese man in modern casual business attire with friendly expression in minimalist office',
    bio: 'Bilingual expert helping students understand Japanese and Chinese cultural nuances. Provides language preparation guidance and cultural orientation sessions.',
    countries: ['Japan', 'China']
  }];


  return (
    <>
      <section className={`bg-background py-16 md:py-24 hidden ${className}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Meet Our Expert Team
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Like having a worldly older sibling who's successfully navigated international education—our team combines deep cultural understanding with professional expertise to guide your journey.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {teamMembers.map((member) =>
            <TeamCard key={member.id} member={member} onSelect={setSelectedMember} />
            )}
          </div>
        </div>
      </section>

      {selectedMember &&
      <div
        className="fixed inset-0 bg-foreground/80 z-50 flex items-center justify-center p-4"
        onClick={() => setSelectedMember(null)}>

          <div
          className="bg-card rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}>

            <div className="relative h-64 overflow-hidden">
              <AppImage
              src={selectedMember.image}
              alt={selectedMember.alt}
              className="w-full h-full object-cover" />

              <button
              onClick={() => setSelectedMember(null)}
              className="absolute top-4 right-4 w-10 h-10 bg-card rounded-full flex items-center justify-center hover:bg-muted transition-colors">

                <Icon name="XMarkIcon" size={20} />
              </button>
            </div>
            <div className="p-8">
              <h3 className="text-2xl font-bold text-foreground mb-2">{selectedMember.name}</h3>
              <p className="text-primary font-semibold mb-1">{selectedMember.role}</p>
              <p className="text-muted-foreground mb-6">{selectedMember.specialization}</p>
              <p className="text-foreground leading-relaxed mb-6">{selectedMember.bio}</p>
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-3">Destination Expertise:</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedMember.countries.map((country, idx) =>
                <span key={idx} className="px-4 py-2 bg-primary/10 text-primary rounded-lg text-sm font-medium">
                      {country}
                    </span>
                )}
                </div>
              </div>
            </div>
          </div>
        </div>
      }
    </>);

};

export default TeamSection;